import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import {
  Badge,
  Button,
  Empty,
  ErrorBlock,
  Input,
  OrderStatus,
  Price,
  Spinner,
} from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { useIdentities } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopShip } from "./desktop/ship.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  要我发的货
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **发货是手机上最该能做的一件事 —— 人就在仓库里。**
 *    以前这个功能只有桌面版有，供应商在货架前面拿着手机却填不了单号，
 *    得回办公室开电脑。而「上架几十个 SKU」那种活才真的需要电脑。
 *
 * ⚠️ 这个列表按 `shipBy` 决定查谁的单（供应商直发 / 卖家自己备货发货），
 *    具体归谁**由服务端判**，前端不复制这条规则 —— 两处迟早走岔。
 */

interface FulfillOrder {
  id: number;
  orderNo: string;
  payment: string;
  fulfillment: string;
  shipName: string;
  shipPhone: string;
  shipAddress: Record<string, string>;
  shippingCarrier?: string | null;
  trackingNo?: string | null;
  createdAt: string;
  items: {
    id: number;
    titleSnapshot: string;
    specsSnapshot: Record<string, string> | null;
    quantity: number;
  }[];
}

/** 发货表单。⚠️ 承运商和单号都必填 —— 少一个买家就查不到物流。 */
function ShipBox({ orderId, orgId, onDone }: { orderId: number; orgId: number; onDone: () => void }) {
  const [carrier, setCarrier] = useState("");
  const [trackingNo, setTrackingNo] = useState("");

  const ship = useMutation({
    mutationFn: () =>
      orgFetch(`/supply-orders/${orderId}/ship`, orgId, {
        method: "POST",
        body: JSON.stringify({ carrier, trackingNo }),
      }),
    onSuccess: onDone,
  });

  return (
    <div className="mt-3 space-y-2 border-t border-border pt-3">
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="承运商" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        <Input
          placeholder="快递单号"
          value={trackingNo}
          onChange={(e) => setTrackingNo(e.target.value)}
        />
      </div>
      {ship.error && <ErrorBlock error={ship.error} />}
      <Button
        className="w-full"
        loading={ship.isPending}
        disabled={!carrier.trim() || !trackingNo.trim()}
        onClick={() => ship.mutate()}
      >
        确认发货
      </Button>
    </div>
  );
}

function Ship() {
  const search = useSearch({ from: "/ship" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();
  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;

  /** 只看待发的，还是全部。⚠️ 默认只看待发 —— 他打开这一页就是来发货的。 */
  const [onlyPending, setOnlyPending] = useState(true);

  const list = useQuery({
    queryKey: ["merchant", orgId, "supply-orders", onlyPending],
    queryFn: () =>
      orgFetch<{ items: FulfillOrder[] }>(
        `/supply-orders?limit=50${onlyPending ? "&fulfillment=pending" : ""}`,
        orgId!,
      ),
    enabled: Boolean(orgId),
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。**
   *    下面那个查询在 `orgId` 为空时是 disabled 的，而 disabled 的查询
   *    `isPending` 恒为 true —— 未登录、或登录了但名下没有店的人，
   *    看到的是一个**永远停不下来的圈**，页面上没有任何提示也没有出路。
   */
  if (!isPending && !orgId) {
    return (
      <SubPage title="要我发的货">
        <Empty title="你还不是商家" hint="发货是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} />
      </SubPage>
    );
  }
  if (isPending || list.isPending) return <SubPage title="要我发的货"><Spinner /></SubPage>;
  if (list.error) return <SubPage title="要我发的货"><ErrorBlock error={list.error} /></SubPage>;

  const items = list.data?.items ?? [];

  return (
    <SubPage title="要我发的货">
      <div className="flex gap-2 px-3 py-3">
        {[
          [true, "待发货"],
          [false, "全部"],
        ].map(([v, label]) => (
          <button
            key={String(v)}
            onClick={() => setOnlyPending(v as boolean)}
            className={
              "rounded-full px-3.5 py-1.5 text-[13px] " +
              (onlyPending === v ? "bg-brand-600 text-white" : "bg-secondary text-muted-foreground")
            }
          >
            {label as string}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <Empty
          title={onlyPending ? "没有要发的货" : "还没有订单"}
          hint={onlyPending ? "客户下单并付款后会出现在这里" : undefined}
        />
      ) : (
        <div className="space-y-2.5 px-3 pb-3">
          {items.map((o) => (
            <Card key={o.id} className="px-4 py-3.5">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[12px] text-muted-foreground">{o.orderNo}</span>
                <OrderStatus payment={o.payment} fulfillment={o.fulfillment} />
              </div>

              {o.items.map((it) => (
                <div key={it.id} className="mt-2 flex items-start justify-between gap-3">
                  <span className="min-w-0 flex-1 text-[14px]">
                    {it.titleSnapshot}
                    {it.specsSnapshot && (
                      <span className="ml-1.5 text-[12px] text-muted-foreground">
                        {Object.values(it.specsSnapshot).join(" / ")}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-[13px] text-muted-foreground">×{it.quantity}</span>
                </div>
              ))}

              {/*
                🔴 **收货信息要完整给到发货的人。**
                   他要照着这个填快递单 —— 手机号打码、地址省略的话他根本发不出去。
                   （跟商家订单列表那边不同：那里是给他看"卖了什么"，不是发货。）
              */}
              <div className="mt-3 border-t border-border pt-2.5 text-[13px] leading-relaxed">
                <p>
                  {o.shipName} <span className="ml-2 text-muted-foreground">{o.shipPhone}</span>
                </p>
                <p className="text-muted-foreground break-anywhere">
                  {o.shipAddress.province}
                  {o.shipAddress.city}
                  {o.shipAddress.district} {o.shipAddress.detail}
                </p>
              </div>

              {o.trackingNo ? (
                <p className="mt-2 text-[13px]">
                  <span className="text-muted-foreground">已发</span>{" "}
                  <span className="font-mono">
                    {o.shippingCarrier} {o.trackingNo}
                  </span>
                </p>
              ) : (
                /**
                 * ⚠️ 只在**已付款且待发货**时出发货框。
                 *    未付款就给输入框的话，他填了也发不出去（服务端会拒）——
                 *    看得见、点得动、就是不成，是最糟的交互。
                 */
                o.payment === "paid" &&
                o.fulfillment === "pending" &&
                orgId && (
                  <ShipBox
                    orderId={o.id}
                    orgId={orgId}
                    onDone={() =>
                      qc.invalidateQueries({ queryKey: ["merchant", orgId, "supply-orders"] })
                    }
                  />
                )
              )}

              {o.payment !== "paid" && !o.trackingNo && (
                <p className="mt-2 text-[12px] text-muted-foreground">
                  等客户付款后才能发货。
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function ShipRoute() {
  return useIsDesktop() ? <DesktopShip /> : <Ship />;
}

export const shipRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ship",
  component: ShipRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
